import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const RiteDepartmentList = ({ departments = [], onOpen, onRefresh }) => {

  const token = getDecryptedAccessToken();

  const [search, setSearch] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const filteredDepartments = useMemo(() => {

    if (!search) return departments;

    const q = search.toLowerCase();

    return departments.filter((d) =>
      d.title?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.contact_email?.toLowerCase().includes(q) ||
      d.phone?.toLowerCase().includes(q)
    );

  }, [search, departments]);

  const handleDelete = async () => {

    try {

      await ApiServices.delete(
        `/api/v1/admin/departments/${deleteItem.id}/`,
        { Authorization: `Bearer ${token}` }
      );

      setSnackbar({
        open: true,
        message: "Department deleted successfully",
        severity: "success"
      });

      setDeleteItem(null);
      onRefresh();

    } catch (error) {

      setSnackbar({
        open: true,
        message: "Failed to delete department",
        severity: "error"
      });

    }

  };

  const columns = [
    {
      field: "title",
      headerName: "Department",
      flex: 1
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5
    },
    {
      field: "contact_email",
      headerName: "Email",
      flex: 1
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => onOpen(params.row)}
          >
            <VisibilityIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => setDeleteItem(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </>
      )
    }
  ];

  return (
    <Box>

      {/* SEARCH */}
      <TextField
        placeholder="Search department..."
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      {/* TABLE */}

      <Box sx={{ height: 450, width: "100%" }}>
        <DataGrid
          rows={filteredDepartments}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Box>

      {/* DELETE CONFIRM DIALOG */}

      <Dialog
        open={Boolean(deleteItem)}
        onClose={() => setDeleteItem(null)}
      >

        <DialogTitle>Delete Department</DialogTitle>

        <DialogContent>

          {deleteItem && (
            <>
              <Typography>
                Are you sure you want to delete this department?
              </Typography>

              <Box mt={2}>

                <Typography>
                  <b>Title:</b> {deleteItem.title}
                </Typography>

                <Typography>
                  <b>Email:</b> {deleteItem.contact_email}
                </Typography>

                <Typography>
                  <b>Phone:</b> {deleteItem.phone}
                </Typography>

              </Box>
            </>
          )}

        </DialogContent>

        <DialogActions>

          <Button onClick={() => setDeleteItem(null)}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
          >
            Delete
          </Button>

        </DialogActions>

      </Dialog>

      {/* SNACKBAR */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default RiteDepartmentList;